/**
 * A student's own details.
 *
 * *** THREE FIELDS, THREE DIFFERENT RULES ***
 * Name changes freely. Email un-verifies itself and is refused outright
 * while a session is booked. Phone is the login credential and cannot be
 * changed here at all — switching it needs OTP proof on the NEW number,
 * which the app does not offer either.
 */

import { useEffect, useState } from 'react'
import { Card } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError, authApi } from '@/lib/api'
import {
  useChangeMyEmail,
  useMyStudentProfile,
  useUpdateMyName,
} from '@/lib/queries'
import styles from './ProfilePage.module.css'

export function StudentProfile() {
  const toast = useToast()
  const profile = useMyStudentProfile()
  const updateName = useUpdateMyName()
  const changeEmail = useChangeMyEmail()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)

  /*
    Seed the inputs once the profile arrives. Keyed on the values rather
    than on the object, so a background refetch does not wipe what the
    student is halfway through typing.
  */
  const loadedName = profile.data?.full_name
  const loadedEmail = profile.data?.email
  useEffect(() => {
    if (loadedName !== undefined) setName(loadedName)
  }, [loadedName])
  useEffect(() => {
    if (loadedEmail != null) setEmail(loadedEmail)
  }, [loadedEmail])

  if (profile.isLoading) {
    return <p className={styles.muted}>Loading your profile…</p>
  }

  if (profile.error) {
    return (
      <p className={styles.error}>
        {profile.error instanceof ApiError
          ? profile.error.message
          : 'Could not load your profile.'}
      </p>
    )
  }

  const p = profile.data
  if (!p) return null

  const nameChanged = name.trim() !== p.full_name && name.trim().length >= 2
  const emailChanged =
    email.trim().length > 0 && email.trim() !== (p.email ?? '')

  const saveName = async () => {
    try {
      await updateName.mutateAsync(name.trim())
      toast.success('Name updated.')
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save that.',
      )
    }
  }

  const saveEmail = async () => {
    try {
      const result = await changeEmail.mutateAsync(email.trim())
      /*
        *** A FAILED SEND MUST NOT BE REPORTED AS SUCCESS ***
        The change is committed either way, but the student's next action
        depends on whether a code actually left. SMTP fails quietly, and
        someone told "code sent" will wait instead of asking again.
      */
      if (result.verification_email_sent) {
        toast.success(`Code sent to ${email.trim()}.`)
      } else {
        toast.error(
          'Your email was changed, but the code could not be sent. Use Resend below.',
        )
      }
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not change your email.',
      )
    }
  }

  const verify = async () => {
    setVerifying(true)
    try {
      await authApi.verifyMyEmail(code.trim())
      toast.success('Email verified.')
      setCode('')
      await profile.refetch()
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'That code did not work.',
      )
    } finally {
      setVerifying(false)
    }
  }

  const resend = async () => {
    setResending(true)
    try {
      await authApi.resendMyEmailCode()
      toast.success('Code sent again.')
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not send the code.',
      )
    } finally {
      setResending(false)
    }
  }

  return (
    <>
      <Card>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="profile-name">
            Your name
          </label>
          <div className={styles.row}>
            <input
              id="profile-name"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoComplete="name"
            />
            <Button
              variant="secondary"
              onClick={saveName}
              disabled={!nameChanged}
              loading={updateName.isPending}
            >
              Save
            </Button>
          </div>
          <p className={styles.hint}>
            This is the name your mentor sees.
          </p>
        </div>
      </Card>

      <Card>
        <div className={styles.field}>
          <span className={styles.labelRow}>
            <label className={styles.label} htmlFor="profile-email">
              Email
            </label>
            <Pill tone={p.email_verified ? 'success' : 'warning'} subtle>
              {p.email_verified ? 'Verified' : 'Not verified'}
            </Pill>
          </span>

          <div className={styles.row}>
            <input
              id="profile-email"
              type="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!p.can_change_email}
              maxLength={255}
              autoComplete="email"
            />
            <Button
              variant="secondary"
              onClick={saveEmail}
              disabled={!emailChanged || !p.can_change_email}
              loading={changeEmail.isPending}
            >
              Change
            </Button>
          </div>

          {/*
            *** SAY WHY IT IS DISABLED ***
            The server refuses this while a session is booked, because a
            changed address is unverified and booking is gated on
            verification — the student would lose access to a session they
            already hold. A greyed field with no reason is the failure to
            avoid.
          */}
          {!p.can_change_email && (
            <p className={styles.hint}>
              You cannot change your email while you have{' '}
              {p.upcoming_session_count === 1
                ? 'a session'
                : `${p.upcoming_session_count} sessions`}{' '}
              booked. Your meeting invitation goes to this address.
            </p>
          )}

          {p.can_change_email && (
            <p className={styles.hint}>
              Changing this sends a code to the new address, and your email
              stays unverified until you enter it.
            </p>
          )}
        </div>

        {/*
          The code box, shown whenever the address is unverified — including
          on arrival, since a student can reach this page mid-verification.
        */}
        {!p.email_verified && p.email && (
          <div className={styles.verify}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="profile-code">
                Enter the {6}-digit code
              </label>
              <div className={styles.row}>
                <input
                  id="profile-code"
                  className={styles.input}
                  value={code}
                  onChange={(e) =>
                    /* Digits only, and never more than six. */
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
                <Button
                  variant="primary"
                  onClick={verify}
                  disabled={code.length !== 6}
                  loading={verifying}
                >
                  Verify
                </Button>
              </div>
              <p className={styles.hint}>
                The code expires after {p.code_expiry_minutes} minutes.{' '}
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={resend}
                  disabled={resending}
                >
                  {resending ? 'Sending…' : 'Send it again'}
                </button>
              </p>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div className={styles.field}>
          <span className={styles.labelRow}>
            <span className={styles.label}>Phone</span>
            <Pill tone={p.phone_verified ? 'success' : 'warning'} subtle>
              {p.phone_verified ? 'Verified' : 'Not verified'}
            </Pill>
          </span>
          <p className={styles.readOnly}>{p.phone ?? '—'}</p>
          {/*
            Read-only on purpose. The phone is the login credential, and
            switching it needs OTP proof on the NEW number — a flow neither
            client offers. Said plainly rather than shown as a dead input.
          */}
          <p className={styles.hint}>
            You sign in with this number. Ask your institute to change it.
          </p>
        </div>
      </Card>
    </>
  )
}
